import { useEffect, useState } from "react";
import { ROLE_LABELS, TENANT_ROLES, type TenantRole } from "@aigov/domain";
import { api, type Member } from "../api";
import { ErrorNote, Pill, errMsg } from "../ui";

export function MembersView({ tenantId, canManage, toast }: { tenantId: string; canManage: boolean; toast: (m: string) => void }) {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("contributor");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { api<Member[]>("GET", `/tenants/${tenantId}/members`).then(setMembers).catch((e) => setError(errMsg(e))); }, [tenantId]);

  const act = async (fn: () => Promise<Member[]>, done: string) => {
    setError(null);
    try { setMembers(await fn()); toast(done); } catch (e) { setError(errMsg(e)); }
  };

  return (
    <div className="stack">
      {canManage && (
        <section className="panel">
          <h2 style={{ marginBottom: 10 }}>Invite someone</h2>
          <form className="row" onSubmit={(e) => { e.preventDefault(); void act(() => api("POST", `/tenants/${tenantId}/members`, { email, role }), "Invitation added").then(() => setEmail("")); }}>
            <input className="input" id="inv-email" type="email" required placeholder="name@client.com" value={email} onChange={(e) => setEmail(e.target.value)} style={{ maxWidth: 280 }} />
            <select className="input" id="inv-role" value={role} onChange={(e) => setRole(e.target.value as TenantRole)} style={{ maxWidth: 180 }}>
              {TENANT_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r].name}</option>)}
            </select>
            <button className="btn primary">Invite</button>
          </form>
          <p className="muted small" style={{ marginBottom: 0 }}>They get access the first time they sign in with this email address, once the identity server has verified it. Create their account in Keycloak or let them self-register.</p>
        </section>
      )}
      <ErrorNote error={error} />
      <section className="panel">
        <h2 style={{ marginBottom: 6 }}>Members</h2>
        {!members ? <p className="muted">Loading…</p> : (
          <div className="scroll"><table>
            <thead><tr><th>Person</th><th>Role</th><th>Status</th><th>Last seen</th>{canManage && <th />}</tr></thead>
            <tbody>{members.map((m) => (
              <tr key={m.id}>
                <td><strong>{m.name || m.email}</strong>{m.name && <div className="muted small">{m.email}</div>}</td>
                <td>{canManage ? (
                  <select className="input" aria-label={`Role for ${m.email}`} value={m.role}
                    onChange={(e) => act(() => api("PATCH", `/tenants/${tenantId}/members/${m.id}`, { role: e.target.value }), "Role updated")}>
                    {TENANT_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r].name}</option>)}
                  </select>) : ROLE_LABELS[m.role].name}
                  <div className="muted small">{ROLE_LABELS[m.role].summary}</div></td>
                <td><Pill tone={m.status === "active" ? "ok" : "mute"}>{m.status === "active" ? "Active" : "Invited"}</Pill></td>
                <td className="small">{m.lastSeen ? new Date(m.lastSeen).toLocaleDateString("en-GB") : "Never"}</td>
                {canManage && <td><button className="btn ghost danger" onClick={() => act(() => api("DELETE", `/tenants/${tenantId}/members/${m.id}`), "Removed")}>Remove</button></td>}
              </tr>))}
            </tbody>
          </table></div>
        )}
      </section>
    </div>
  );
}
